// src/pages/ServiceDetails.js
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import AnimatedButton from "../components/AnimatedButton";
import housekeepingImg from "../assets/images/services/housekeeping.png";
import services1 from "../assets/images/services/services-1.png";
import services2 from "../assets/images/services/services-2.png";
import services3 from "../assets/images/services/services-3.png";
import services4 from "../assets/images/services/services-4.png";
import services5 from "../assets/images/services/services-5.png";
import labourImg from "../assets/images/services/labour.png";
import facadeImg from "../assets/images/services/facade-2.png";
import projectImg from "../assets/images/services/project-cleaning.png";
import staffingImg from "../assets/images/services/staffing-1.png";
import maidsImg from "../assets/images/services/maid-1.jpg";
import useScrollToTop from "../hooks/useScrollToTop";
import ClientsMarquee from "../components/ClientsMarquee";

const services = [
  {
    slug: "corporate-housekeeping",
    title: "Corporate Housekeeping",
    bannerTitle: "Premium Corporate Housekeeping",
    bannerDescription:
      "Keeping your workplace spotless, professional, and productive with eco-friendly practices and expert staff.",
    description:
      "Professional cleaning tailored for office spaces, ensuring a spotless environment that promotes productivity and a healthy workplace. Our expert team uses eco-friendly products and advanced cleaning techniques to maintain hygiene, enhance aesthetics, and create a welcoming atmosphere for employees and clients alike.",
    coreFeatures: [
      "Eco-friendly cleaning products",
      "Regular maintenance schedules",
      "Sanitization of high-touch areas",
      "Waste management and recycling",
      "Customizable service plans",
    ],
    image: services2,
    gallery: [
      housekeepingImg,
      housekeepingImg,
      housekeepingImg,
      housekeepingImg,
    ],
    richText: [
      {
        title: "Eco-Friendly Practices",
        content:
          "We prioritize health and sustainability by using eco-certified cleaning products that are safe, effective, and sustainable for office environments.",
      },
      {
        title: "Enhanced Productivity",
        content:
          "A clean and well-maintained workplace improves focus, employee morale, and overall productivity.",
      },
      {
        title: "Custom Service Plans",
        content:
          "We design cleaning schedules around your office hours, ensuring minimal disruption and maximum efficiency.",
      },
    ],
  },
  {
    slug: "industrial-housekeeping",
    title: "Industrial Housekeeping",
    bannerTitle: "Heavy-Duty Industrial Housekeeping",
    bannerDescription:
      "Safe, compliant, and efficient cleaning solutions for factories and warehouses.",
    description:
      "Specialized cleaning services for factories and warehouses designed to uphold safety standards and operational efficiency. RR Facility Management focuses on removing hazardous waste, dust, and debris while implementing thorough sanitation protocols that comply with industrial regulations, helping to reduce downtime and protect your workforce.",
    coreFeatures: [
      "Hazardous waste removal",
      "Dust and debris clearing",
      "Compliance with safety regulations",
      "Heavy-duty equipment cleaning",
      "Scheduled and emergency cleaning",
    ],
    image: services3,
    gallery: [labourImg, labourImg, labourImg, labourImg],
    richText: [
      {
        title: "Safety First",
        content:
          "We strictly follow industrial safety regulations to ensure the workplace remains hazard-free and compliant.",
      },
      {
        title: "Specialized Equipment",
        content:
          "Our team uses advanced industrial-grade tools for deep cleaning heavy machinery, work floors, and production areas.",
      },
      {
        title: "Downtime Reduction",
        content:
          "Scheduled cleaning ensures minimal disruption to your production workflow and maximizes operational uptime.",
      },
    ],
  },
  {
    slug: "facade-cleaning",
    title: "Facade Cleaning",
    bannerTitle: "Restore Your Building's Shine",
    bannerDescription:
      "Enhance curb appeal and structural life with expert facade cleaning solutions.",
    description:
      "Restore the shine and enhance the curb appeal of your building exterior with our expert facade cleaning solutions. Using high-quality equipment and eco-friendly cleaning agents, we remove dirt, pollution stains, mold, and algae to preserve structural integrity and improve your property's visual impact for visitors and tenants.",
    coreFeatures: [
      "Eco-friendly cleaning agents",
      "Removal of pollution stains and mold",
      "High-quality equipment",
      "Protection of building structure",
      "Improved curb appeal",
    ],
    image: services4,
    gallery: [facadeImg, facadeImg, facadeImg, facadeImg],
    richText: [
      {
        title: "Curb Appeal Matters",
        content:
          "A clean and shining exterior leaves a positive impression on clients, tenants, and visitors.",
      },
      {
        title: "Structural Protection",
        content:
          "Regular facade cleaning prevents damage from mold, algae, and pollutants, extending the life of your building.",
      },
      {
        title: "Eco-Friendly Solutions",
        content:
          "We use environmentally safe cleaning agents that deliver spotless results without harming the surroundings.",
      },
    ],
  },
  {
    slug: "project-cleaning",
    title: "Project Cleaning",
    bannerTitle: "Post-Construction Project Cleaning",
    bannerDescription:
      "Get your site move-in ready with our comprehensive project cleaning services.",
    description:
      "Comprehensive post-construction cleaning services that prepare your site for immediate occupancy. Our team expertly clears debris, dust, and leftover materials, ensuring all surfaces, windows, and floors are pristine. Trust RR Facility Management to deliver a safe, clean, and welcoming environment that meets the highest standards for your new space.",
    coreFeatures: [
      "Debris and dust removal",
      "Surface and window cleaning",
      "Floor polishing and cleaning",
      "Safe disposal of construction waste",
      "Ready-for-occupancy preparation",
    ],
    image: services5,
    gallery: [projectImg, projectImg, projectImg, projectImg],
    richText: [
      {
        title: "Debris Management",
        content:
          "We ensure all construction debris, dust, and leftover materials are removed safely and efficiently.",
      },
      {
        title: "Attention to Detail",
        content:
          "From window cleaning to floor polishing, we prepare your site to meet the highest hygiene and aesthetic standards.",
      },
      {
        title: "Move-In Ready",
        content:
          "Your new property will be clean, safe, and immediately ready for occupancy.",
      },
    ],
  },
  {
    slug: "staffing-services",
    title: "Staffing Services",
    bannerTitle: "Reliable Facility Staffing",
    bannerDescription:
      "Trained, verified, and dependable manpower for housekeeping and facility management.",
    description:
      "Reliable, trained manpower supply for housekeeping, cleaning, and facility management. Our staffing services ensure that you get trustworthy and skilled personnel tailored to your business needs.",
    coreFeatures: [
      "Trained manpower",
      "Flexible staffing plans",
      "Background verified staff",
      "On-demand service",
    ],
    image: services1,
    gallery: [staffingImg, staffingImg, staffingImg, staffingImg],
    richText: [
      {
        title: "Verified Workforce",
        content:
          "All our staff members undergo strict background checks to ensure reliability and trustworthiness.",
      },
      {
        title: "Flexible Deployment",
        content:
          "Whether you need daily housekeeping staff or short-term project support, we adapt to your requirements.",
      },
      {
        title: "Continuous Training",
        content:
          "Our team members are regularly trained in modern cleaning techniques, safety protocols, and customer service.",
      },
    ],
  },
  {
    slug: "overseas-maid-services",
    title: "Overseas Maids Services",
    bannerTitle: "Trusted Overseas Maids Recruitment & Services",
    bannerDescription:
      "Reliable and skilled overseas maids to support households and businesses with professionalism and care.",
    description:
      "Our Overseas Maids Services provide trained and experienced domestic helpers who meet international standards. We specialize in connecting families and businesses with trustworthy maids who excel in housekeeping, childcare, elderly care, and general domestic assistance, ensuring peace of mind and quality service.",
    coreFeatures: [
      "Skilled and trained domestic helpers",
      "Childcare and elderly care support",
      "Professional housekeeping services",
      "Screened and verified candidates",
      "Flexible contracts tailored to your needs",
    ],
    image: services5, // replace with actual image variable
    gallery: [maidsImg, maidsImg, maidsImg, maidsImg], // replace with actual maid-related images
    richText: [
      {
        title: "Trusted Recruitment",
        content:
          "We ensure every maid goes through a thorough verification and training process before being placed overseas.",
      },
      {
        title: "Comprehensive Support",
        content:
          "From documentation to deployment, we handle the complete process, making overseas hiring hassle-free.",
      },
      {
        title: "Tailored Services",
        content:
          "Whether for households or businesses, we match you with the right maid services suited to your lifestyle and requirements.",
      },
    ],
  },
];

const ServiceDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = services.find((s) => s.slug === slug);
  useScrollToTop();

  if (!service) {
    return (
      <div className="text-center py-5">
        <h2>Service Not Found</h2>
        <AnimatedButton
          text="Back to Services"
          onClick={() => navigate("/services")}
        />
      </div>
    );
  }

  return (
    <>
      <section className="about-banner position-relative overflow-hidden">
        <div className="position-absolute top-0 start-0 w-100 h-100"></div>
        <div className="container position-relative z-index-2">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h1 className="display-4 fw-bold mb-4 animate__animated animate__fadeInUp">
                {service.bannerTitle}
              </h1>
              <p className="lead  mb-4 animate__animated animate__fadeInUp animate__delay-1s">
                {service.bannerDescription}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-5 bg-light">
        <div className="container">
          {/* Service Overview Card */}
          <div className="card border-0 shadow-lg mb-5">
            <div className="row g-0">
              <div className="col-lg-5">
                <div className="position-relative h-100">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="img-fluid h-100 w-100 object-fit-cover rounded-start"
                  />
                  <div className="position-absolute top-0 start-0 w-100 h-100 bg-gradient opacity-25 rounded-start"></div>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="card-body p-5">
                  <div className="mb-3">
                    <h3 className="fw-bold">{service.title}</h3>
                  </div>
                  <p className="text-muted mb-4">{service.description}</p>

                  {/* Core Features */}
                  {service.coreFeatures && (
                    <div className="mb-4">
                      <h5 className="fw-bold mb-3 text-dark">Key Features</h5>
                      <div className="row">
                        {service.coreFeatures.map((feature, idx) => (
                          <div key={idx} className="col-md-6 mb-2">
                            <div className="d-flex align-items-center">
                              <div
                                className="bg-success rounded-circle me-2"
                                style={{ width: "8px", height: "8px" }}
                              ></div>
                              <span className="text-muted">{feature}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <a href="/contact">
                    <AnimatedButton text="Contact Us" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ClientsMarquee />

      {/* Rich Text Features Section */}
      {service.richText && (
        <section className="py-5 bg-white">
          <div className="container">
            <div className="row justify-content-center mb-5">
              <div className="col-lg-8 text-center">
                <h2 className="display-6 fw-bold mb-3">
                  Why Choose Our Service?
                </h2>
                <p className="text-muted">
                  Discover what makes our {service.title.toLowerCase()} service
                  stand out from the competition
                </p>
              </div>
            </div>

            <div className="row g-4">
              {service.richText.map((item, idx) => (
                <div key={idx} className="col-lg-4">
                  <div className="card h-100 border-0 shadow-sm hover-lift">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div
                          className="bg-primary rounded-circle me-3 d-flex align-items-center justify-content-center"
                          style={{ width: "50px", height: "50px" }}
                        >
                          <span className="text-white fw-bold">{idx + 1}</span>
                        </div>
                        <h5 className="fw-bold mb-0 text-dark">{item.title}</h5>
                      </div>
                      <p className="text-muted mb-0 lh-relaxed">
                        {item.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Gallery Section */}
      {service.gallery && (
        <section className="py-5 bg-light">
          <div className="container">
            <div className="row justify-content-center mb-5">
              <div className="col-lg-8 text-center">
                <h2 className="display-6 fw-bold mb-3">Our Work Gallery</h2>
                <p className="text-muted">
                  See our {service.title.toLowerCase()} service in action
                </p>
              </div>
            </div>

            <div className="row g-4">
              {service.gallery.map((img, idx) => (
                <div key={idx} className="col-lg-3 col-md-6">
                  <div className="gallery-item position-relative overflow-hidden rounded-3 shadow-sm">
                    <img
                      src={img}
                      alt={`${service.title} Gallery ${idx + 1}`}
                      className="img-fluid w-100 h-100 object-fit-cover transition-transform"
                      style={{
                        height: "250px",
                        transition: "transform 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "scale(1.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "scale(1)";
                      }}
                    />
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-0 d-flex align-items-center justify-content-center transition-opacity">
                      <span className="text-white fw-bold">View Image</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action Section */}
      <section
        className="py-5 text-center text-white"
        style={{
          background: "linear-gradient(135deg, #00213eff 0%, #012778ff 100%)",
        }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="display-6 fw-bold mb-3">Ready to Get Started?</h2>
              <p className="lead mb-4 opacity-75">
                Contact us today for a free consultation and customized quote
                for your {service.title.toLowerCase()} needs.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <AnimatedButton text="Get Free Quote" />
                <button
                  className="btn text-light px-4"
                  onClick={() => navigate("/services")}
                >
                  View All Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetails;
